"use client";

import { FormEvent, useState } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";

import { saveTokens, getOrgId } from "@/lib/auth";
import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/toast";
import type { TokenResponse } from "@/types/auth";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const token = await apiRequest<TokenResponse>("/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, org_id: getOrgId() }),
      });
      saveTokens(token.access_token, token.refresh_token);
      toast.success("Sesión iniciada", "Bienvenido de vuelta.");
      router.push("/workspace");
    } catch (err) {
      const message = err instanceof Error ? err.message : "No pudimos verificar tus credenciales.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-[100dvh] flex items-center justify-center p-6 bg-background relative overflow-hidden">
      {/* Subtle ambient background — single soft radial, no aggressive branding */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] bg-primary opacity-[0.05] blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(30_10%_50%_/_0.04)_1px,transparent_1px),linear-gradient(to_bottom,hsl(30_10%_50%_/_0.04)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-[400px] relative z-10"
      >
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg tracking-tight shadow-sm shadow-primary/20 mb-4">
            A
          </div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">
            Inicia sesión en Arsenal Wiki
          </h1>
          <p className="text-sm text-muted-foreground mt-1 text-pretty">
            Base de conocimiento colaborativa para tu equipo.
          </p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border/60 rounded-2xl shadow-sm shadow-black/5 p-6 sm:p-7">
          <form onSubmit={onSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="text-xs font-medium text-foreground/80"
              >
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3.5 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                placeholder="tu@empresa.com"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-xs font-medium text-foreground/80"
                >
                  Contraseña
                </label>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => toast.info("Recuperación de contraseña", "Contacta al administrador de tu organización.")}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-10 pl-3.5 pr-10 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-destructive/8 border border-destructive/20 rounded-lg text-sm text-destructive flex items-start gap-2"
              >
                <span className="flex-1">{error}</span>
              </motion.div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="group w-full h-10 bg-primary text-primary-foreground rounded-lg font-medium text-sm transition-all hover:bg-primary/90 active:scale-[0.99] disabled:opacity-60 disabled:pointer-events-none shadow-sm shadow-primary/15 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando…
                </>
              ) : (
                <>
                  Continuar
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer hint */}
        <p className="text-center text-xs text-muted-foreground/70 mt-6 leading-relaxed">
          ¿No tienes cuenta?{" "}
          <button
            type="button"
            className="text-foreground font-medium hover:underline underline-offset-2"
            onClick={() => toast.info("Solicitar acceso", "Pídele a tu administrador una invitación por correo.")}
          >
            Solicita acceso a tu administrador
          </button>
        </p>
      </motion.div>
    </main>
  );
}